/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip } from '@patternfly/react-core';
import { faExpandAlt, faCompressAlt } from '@fortawesome/pro-regular-svg-icons';
import {
	CHART_TYPE_REGISTRY,
	CHART_CATEGORIES,
	DEFAULT_EXPANDED_CATEGORIES,
	ChartCategory,
	ChartTypeDefinition,
	getChartTypesByCategory,
	getPopularChartTypes,
	filterChartTypes,
} from '../../../helpers/chart-type.registry';
import { ChartTypeSearchInput } from './SearchInput';
import { CategorySection } from './CategorySection';
import { ChartTypeGrid } from './ChartTypeGrid';
import './ChartTypePicker.scss';

interface ChartTypePickerProps {
	selectedType?: string;
	isSingleSeries: boolean;
	onSelectType: (type: string) => void;
	droppedFields?: any[];
}

export const ChartTypePicker: React.FC<ChartTypePickerProps> = ({
	selectedType,
	isSingleSeries,
	onSelectType,
	droppedFields,
}) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [expandedCategories, setExpandedCategories] = useState<Set<ChartCategory>>(
		new Set(DEFAULT_EXPANDED_CATEGORIES)
	);
	const [showAllView, setShowAllView] = useState(false);
	const expandToggleRef = React.useRef<HTMLButtonElement>(null);

	const toggleCategory = (category: ChartCategory) => {
		setExpandedCategories((prev) => {
			const next = new Set(prev);
			if (next.has(category)) {
				next.delete(category);
			} else {
				next.add(category);
			}
			return next;
		});
	};

	const toggleExpandAll = () => {
		if (expandedCategories.size === CHART_CATEGORIES.length) {
			setExpandedCategories(new Set());
		} else {
			setExpandedCategories(new Set(CHART_CATEGORIES));
		}
	};

	// Filter definitions by series type compatibility
	const compatibleDefinitions = useMemo(() => {
		return Object.values(CHART_TYPE_REGISTRY).filter((def) => {
			if (isSingleSeries) {
				return def.seriesType === 'single' || def.seriesType === 'both';
			} else {
				return def.seriesType === 'multi' || def.seriesType === 'both';
			}
		});
	}, [isSingleSeries]);

	// Filter definitions based on search query
	const filteredDefinitions = useMemo(() => {
		if (!searchQuery) {
			return compatibleDefinitions;
		}
		return filterChartTypes(searchQuery).filter((def) => compatibleDefinitions.includes(def));
	}, [searchQuery, compatibleDefinitions]);

	// Get popular types (always visible at top if no search)
	const popularDefinitions = useMemo(() => {
		return getPopularChartTypes().filter((def) => compatibleDefinitions.includes(def));
	}, [compatibleDefinitions]);

	// Get categories with their filtered types
	const categoryMap = useMemo(() => {
		const map = new Map<ChartCategory, ChartTypeDefinition[]>();

		CHART_CATEGORIES.forEach((category) => {
			const types = getChartTypesByCategory(category);
			const filtered = types.filter(
				(t) => filteredDefinitions.includes(t) && compatibleDefinitions.includes(t)
			);
			if (filtered.length > 0) {
				map.set(category, filtered);
			}
		});

		return map;
	}, [filteredDefinitions, compatibleDefinitions]);

	// Get total count of all compatible types
	const totalChartCount = useMemo(() => {
		return filteredDefinitions.length;
	}, [filteredDefinitions]);

	// Show popular section only when not searching and not in all view
	const showPopular = !searchQuery && !showAllView && popularDefinitions.length > 0;

	const seriesStatusLabel =
		droppedFields && droppedFields.length > 1 ? 'Multi-Series' : 'Single-Series';

	return (
		<div className="chart-type-picker">
			<div className="search-and-controls">
				<ChartTypeSearchInput
					value={searchQuery}
					onChange={setSearchQuery}
					onClear={() => setSearchQuery('')}
					placeholder="Search chart types..."
				/>
				<div className="controls-section">
					<div className="series-status">
						<span className="status-label">
							Current Chart Fields status: {seriesStatusLabel}
						</span>
					</div>
					<Button
						ref={expandToggleRef}
						variant="plain"
						className="expand-collapse-btn"
						onClick={toggleExpandAll}
						aria-label={
							expandedCategories.size === CHART_CATEGORIES.length
								? 'Collapse all'
								: 'Expand all'
						}
					>
						<FontAwesomeIcon
							icon={
								expandedCategories.size === CHART_CATEGORIES.length
									? faCompressAlt
									: faExpandAlt
							}
						/>
					</Button>
					<Tooltip
						content={
							expandedCategories.size === CHART_CATEGORIES.length
								? 'Collapse all'
								: 'Expand all'
						}
						reference={expandToggleRef}
					/>
				</div>
			</div>

			<div className="picker-content">
				{/* All View Category */}
				{!searchQuery && (
					<div className="all-category-section">
						<button
							className={`all-category-chip ${
								showAllView ? 'expanded' : 'collapsed'
							}`}
							onClick={() => setShowAllView(!showAllView)}
						>
							<span className="category-name">All</span>
							<span className="type-count">{totalChartCount}</span>
						</button>
						{showAllView && (
							<div className="all-types-grid">
								<ChartTypeGrid
									definitions={filteredDefinitions}
									selectedType={selectedType}
									isSingleSeries={isSingleSeries}
									onSelectType={onSelectType}
								/>
							</div>
						)}
					</div>
				)}

				{showPopular && (
					<div className="popular-section">
						<h3 className="section-title">Popular</h3>
						<div className="popular-grid">
							{popularDefinitions.map((def) => (
								<button
									key={def.type}
									className={`popular-button ${
										selectedType === def.type ? 'selected' : ''
									}`}
									onClick={() => onSelectType(def.type)}
									title={def.label}
								>
									<div className="icon">
										<FontAwesomeIcon icon={def.icon} />
									</div>
									<span>{def.label}</span>
								</button>
							))}
						</div>
					</div>
				)}

				{!showAllView && (
					<div className="categories-section">
						{CHART_CATEGORIES.map((category) => {
							const definitions = categoryMap.get(category);
							if (!definitions || definitions.length === 0) {
								return null;
							}

							return (
								<CategorySection
									key={category}
									category={category}
									definitions={definitions}
									selectedType={selectedType}
									isSingleSeries={isSingleSeries}
									isExpanded={expandedCategories.has(category)}
									onToggle={() => toggleCategory(category)}
									onSelectType={onSelectType}
								/>
							);
						})}
					</div>
				)}

				{filteredDefinitions.length === 0 && (
					<div className="no-results">
						<p>No chart types found matching "{searchQuery}"</p>
					</div>
				)}
			</div>
		</div>
	);
};
